import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { ChevronLeft, ChevronRight, ExternalLink, Star } from 'lucide-react';
import OceanBackground from '@/components/background/OceanBackground';

const MOCK_HOTELS = [
  {
    name: '西湖国宾馆', area: '西湖景区·杨公堤', price: 1680, rating: 4.8, image: 'hotel1',
    tags: ['含早餐', '湖景', '地铁近', '父母友好'],
    scores: { 位置: 96, 景点便利: 94, 交通: 91, 餐饮: 89, 性价比: 92, 综合: 94 },
    aiReason: '虽然价格不是最低，但能减少每天约 60–80 分钟交通时间，且周边晚餐选择更多，更适合父母同行。'
  },
  {
    name: '杭州西湖君悦酒店', area: '湖滨商圈·南山路', price: 1450, rating: 4.7, image: 'hotel2',
    tags: ['湖景房', '五星级', '步行可达'],
    scores: { 位置: 90, 景点便利: 92, 交通: 88, 餐饮: 95, 性价比: 94, 综合: 92 },
    aiReason: '位于湖滨商圈核心，周边餐饮丰富，步行即可到达西湖，适合喜欢热闹和便利的家庭。'
  },
  {
    name: '西子湖四季酒店', area: '曲院风荷·杨公堤', price: 2100, rating: 4.9, image: 'hotel3',
    tags: ['园林', '奢华', '拍照出片'],
    scores: { 位置: 88, 景点便利: 96, 交通: 85, 餐饮: 91, 性价比: 78, 综合: 90 },
    aiReason: '园林式酒店本身就是景点，拍照极佳，步行可达曲院风荷和西泠印社，度假感强。'
  }
];

const HotelIntelligence = () => {
  const [current, setCurrent] = useState(0);
  const hotel = MOCK_HOTELS[current];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #041426 0%, #061A2C 50%, #041426 100%)' }}>
      <Navbar />
      <OceanBackground />
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-20 relative z-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.94)', letterSpacing: '-0.025em' }}>酒店智能决策</h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>让 AI 帮你找到真正适合这次旅行的酒店</p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Hotel Detail */}
          <div className="lg:col-span-3 space-y-5">
            <div className="ocean-glass overflow-hidden">
              {/* Hero Image */}
              <div className="relative h-52 overflow-hidden">
                <img src={`https://picsum.photos/seed/${hotel.image}/800/400`} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 30%, rgba(4,20,38,0.9) 100%)' }} />
                <div className="absolute bottom-4 left-5">
                  <h2 className="text-xl font-semibold" style={{ color: 'white', letterSpacing: '-0.02em' }}>{hotel.name}</h2>
                  <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.7)' }}>{hotel.area} · {hotel.rating} 分</p>
                </div>
                <div className="absolute top-4 right-4 rounded-xl px-3 py-1.5" style={{ background: 'rgba(4,20,38,0.7)', backdropFilter: 'blur(12px)' }}>
                  <span className="text-lg font-bold" style={{ color: '#D8B15A' }}>¥{hotel.price}</span>
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>/晚</span>
                </div>
              </div>

              {/* Info */}
              <div className="p-5">
                <div className="flex gap-2 mb-4 flex-wrap">
                  {hotel.tags.map(t => (
                    <span key={t} className="text-xs px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}>{t}</span>
                  ))}
                </div>

                {/* AI Reason */}
                <div className="rounded-xl p-4 mb-5" style={{ background: 'rgba(57,201,130,0.06)', border: '1px solid rgba(57,201,130,0.12)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'rgba(57,201,130,0.15)' }}>
                      <Star className="w-3 h-3" style={{ color: '#39C982' }} />
                    </div>
                    <span className="text-xs font-medium" style={{ color: 'rgba(57,201,130,0.9)' }}>AI 推荐</span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>{hotel.aiReason}</p>
                  <p className="text-xs font-semibold mt-2" style={{ color: '#39C982' }}>推荐度 {hotel.scores.综合}</p>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button className="flex-1 btn-primary text-sm h-10 flex items-center justify-center gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5" /> 查看详情
                  </button>
                  <button className="flex-1 btn-ghost text-sm h-10">加入对比</button>
                </div>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-center gap-4">
              <button onClick={() => setCurrent(c => Math.max(0, c - 1))}
                className="w-8 h-8 rounded-full ocean-glass flex items-center justify-center">
                <ChevronLeft className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.6)' }} />
              </button>
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{current + 1} / {MOCK_HOTELS.length}</span>
              <button onClick={() => setCurrent(c => Math.min(MOCK_HOTELS.length - 1, c + 1))}
                className="w-8 h-8 rounded-full ocean-glass flex items-center justify-center">
                <ChevronRight className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.6)' }} />
              </button>
            </div>
          </div>

          {/* Right: Scores + Comparison */}
          <div className="lg:col-span-2 space-y-5">
            <div className="ocean-glass p-5">
              <h3 className="text-sm font-semibold mb-4" style={{ color: 'rgba(255,255,255,0.9)' }}>AI SCORE</h3>
              <div className="text-center mb-5">
                <span className="text-4xl font-light" style={{ color: '#D8B15A', letterSpacing: '-0.03em' }}>{hotel.scores.综合}</span>
              </div>
              <div className="space-y-3">
                {Object.entries(hotel.scores).filter(([k]) => k !== '综合').map(([dim, score]) => (
                  <div key={dim}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{dim}</span>
                      <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.85)' }}>{score}</span>
                    </div>
                    <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div className="h-full rounded-full transition-all duration-500" style={{
                        width: `${score}%`,
                        background: score >= 90 ? 'linear-gradient(90deg, #39C982, #4AA7D8)' : score >= 80 ? '#4AA7D8' : '#D8B15A'
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Comparison */}
            <div className="ocean-glass p-5">
              <h3 className="text-sm font-semibold mb-3" style={{ color: 'rgba(255,255,255,0.9)' }}>横向对比</h3>
              <div className="space-y-2">
                {MOCK_HOTELS.map((h, i) => (
                  <button key={i} onClick={() => setCurrent(i)}
                    className={`w-full text-left p-3 rounded-xl transition-all duration-200 ${
                      i === current ? 'ring-1' : ''
                    }`}
                    style={{
                      background: i === current ? 'rgba(74,167,216,0.1)' : 'rgba(255,255,255,0.03)',
                      borderColor: i === current ? 'rgba(74,167,216,0.25)' : 'transparent',
                    }}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.85)' }}>{h.name}</span>
                      <span className="text-xs font-bold" style={{ color: '#D8B15A' }}>{h.scores.综合}</span>
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>¥{h.price}/晚 · {h.rating}分</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelIntelligence;
